import React from 'react';
import DataTable from '../../components/common/DataTable';
import AccessKeyDisplay from './AccessKeyDisplay';
import { getAddressParts } from '../../../shared/utils/formatAddress';
import { formatDateTime } from '../../../shared/utils/formatDate';
import { formatTaskPeopleLine } from '../../utils/taskTicketDisplay';
import StatusPill from '../../../shared/components/pill/StatusPill';
import useIsNarrowViewport from '../../../shared/hooks/useIsNarrowViewport';

// Helper to compute remaining days
function daysRemaining(until) {
  if (!until) return '—';
  const end = new Date(until);
  const now = new Date();
  const diffMs = end - now;
  if (diffMs <= 0) return 'Expired';
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return `${days} day${days !== 1 ? 's' : ''}`;
}

export default function TasksTable({ items, onAssign, emptyMessage = 'No open tasks found.', filterExtras = null }) {
  const isNarrow = useIsNarrowViewport();
  const columns = [
    {
      key: 'workflow_name',
      label: 'Workflow',
      sortable: true,
      role: 'primary',
      // Was "Definition #15 · published 20 Oct 2026 · deprecated". The row id was the only
      // version the API could offer at the time; `v4` is the same fact in the admin's own terms.
      // The publish date goes on a narrow screen — it is the least of the three things here and
      // the one that costs the most width — and the lifecycle word goes everywhere: a task runs
      // against the version it was created against, so whether that version has since been
      // superseded says nothing about the task.
      render: (row) => {
        const wf = row.workflow;
        const version = Number.isFinite(wf?.version) ? `v${wf.version}` : null;
        const published = wf?.published_at ? `published ${formatDateTime(wf.published_at)}` : null;
        const detail = isNarrow ? [version] : [version, published];
        const detailLine = detail.filter(Boolean).join(' · ');

        return (
          <div className="d-flex align-items-start">
            <i className="bi bi-list-check text-primary me-2 mt-1" />
            <div className="min-w-0">
              <div className="fw-semibold">{wf?.name || '—'}</div>
              {detailLine && (
                <div className="text-muted small text-break">{detailLine}</div>
              )}
            </div>
          </div>
        );
      },
      filterValue: (row) =>
        [row.workflow?.name, row.workflow?.version && `v${row.workflow.version}`, row.workflow?.id]
          .filter(Boolean)
          .join(' '),
    },
    {
      key: 'task_status',
      label: 'Status',
      sortable: true,
      role: 'secondary',
      render: (row) =>
        row.completed_at ? (
          <StatusPill tone="neutral">Closed</StatusPill>
        ) : (
          <StatusPill tone="success">Open</StatusPill>
        ),
      filterValue: (row) => (row.completed_at ? 'closed' : 'open'),
    },
    {
      key: 'asset_name',
      label: 'Asset',
      sortable: true,
      render: row => <span>{row.asset?.name || '—'}</span>,
      filterValue: row => row.asset?.name || ''
    },
    {
      key: 'asset_address',
      label: 'Address',
      sortable: false,
      render: row => {
        const parts = getAddressParts(row.asset || {});
        if (parts.length === 0) return <span className="text-muted">—</span>;
        return (
          <div>
            <div>{parts[0]}</div>
            {parts[1] && <div className="text-muted small">{parts[1]}</div>}
          </div>
        );
      },
      filterValue: row => getAddressParts(row.asset || {}).join(' ')
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      sortable: true,
      render: row => {
        const peopleLine = formatTaskPeopleLine(row);
        if (peopleLine) {
          return <span>{peopleLine}</span>;
        }
        return (
          <span className="d-flex align-items-center gap-2">
            <span className="text-muted">Unassigned</span>
            {onAssign && (
              <button
                className="btn btn-sm btn-outline-primary py-0 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssign(row);
                }}
              >
                Assign
              </button>
            )}
          </span>
        );
      },
      filterValue: row => formatTaskPeopleLine(row) || row.assigned_to_email || 'unassigned',
    },
    {
      key: 'access_remaining',
      label: 'Remaining Access',
      sortable: true,
      className: 'text-end',
      render: row => {
        const expiresAt = row.access_key?.expires_at || row.access_duration_until;
        return <span>{daysRemaining(expiresAt)}</span>;
      },
      filterValue: row => {
        const expiresAt = row.access_key?.expires_at || row.access_duration_until;
        return daysRemaining(expiresAt);
      }
    },
    {
      key: 'access_key_display',
      label: 'Access Key',
      sortable: false,
      // A long monospaced token, and not what anyone scans a list for on a phone; the task
      // detail page has it.
      hideOnNarrow: true,
      render: row => {
        const accessKey = row.access_key?.key || row.access_key;
        const expiresAt = row.access_key?.expires_at || row.access_duration_until;
        /* eslint-disable jsx-a11y/no-access-key -- domain prop: access-key token, not HTML accesskey */
        return (
          <AccessKeyDisplay
            accessKey={accessKey}
            accessDurationUntil={expiresAt}
          />
        );
        /* eslint-enable jsx-a11y/no-access-key */
      },
      filterValue: row => {
        const accessKey = row.access_key?.key || row.access_key;
        const expiresAt = row.access_key?.expires_at || row.access_duration_until;
        const isExpired = expiresAt && new Date(expiresAt) <= new Date();
        return (!accessKey || isExpired) ? '' : accessKey;
      }
    }
  ];

  return (
    <DataTable
      items={items}
      columns={columns}
      filterPlaceholder="Search tasks…"
      emptyMessage={emptyMessage}
      filterExtras={filterExtras}
    />
  );
}
